import React, { useContext, useMemo, createContext, useState } from "react";
import Conversation, { TConversation } from "../../models/conversation";
import createConversationApi, {
  TCreateConversationPayload,
} from "../../lib/create-conversation";
import { AppSettings } from "../../lib/get-app-settings";
import useRefCallback from "use-ref-callback";
import Customer from "../../models/customer";
import loadConversations, {
  TConversationListFilter,
} from "../../lib/load-conversations";
import { useConversationListParameters } from "../../hooks/use-conversation-list-parameters";

const CustomerPortalContext = createContext<CustomerPortalContextProps>(
  {} as CustomerPortalContextProps
);

const useCustomerPortalContext = () => useContext(CustomerPortalContext);

export const CustomerPortalContextProvider = ({
  appSettings,
  customer,
  children,
  initial,
}: {
  appSettings: AppSettings;
  customer: Customer | null;
  children: React.ReactNode;
  initial: {
    customerConversations: Conversation[];
    customerTotal: number;
    accountConversations: Conversation[];
    accountTotal: number;
  };
}) => {
  const [conversations, setConversations] = useState<
    Record<ConversationTab, Conversation[]>
  >(() => ({
    [ConversationTab.customer]: initial.customerConversations,
    [ConversationTab.account]: initial.accountConversations,
  }));

  const [cursor, setCursor] = useState<Record<ConversationTab, number>>(() => ({
    [ConversationTab.customer]: initial.customerConversations.length,
    [ConversationTab.account]: initial.accountConversations.length,
  }));

  const [totals, setTotals] = useState<Record<ConversationTab, number>>(() => ({
    [ConversationTab.customer]: initial.customerTotal,
    [ConversationTab.account]: initial.accountTotal,
  }));

  const [loading, setLoading] = useState<Record<ConversationTab, boolean>>(
    () => ({
      [ConversationTab.customer]: false,
      [ConversationTab.account]: false,
    })
  );

  const [{ filters, sorts }, setParameters] = useConversationListParameters();

  const setFilter = (
    tab: ConversationTab,
    filter?: TConversationListFilter
  ) => {
    setParameters((prev) => ({
      filters: { ...prev.filters, [tab]: filter },
    }));
  };

  const setSort = (tab: ConversationTab, sort: string) => {
    setParameters((prev) => ({
      sorts: { ...prev.sorts, [tab]: sort },
    }));
  };

  const updateConversationsOnTicketSubmit = (conversation: TConversation) => {
    if (
      !conversations[ConversationTab.customer].find(
        (c) => c.id === conversation.id
      )
    ) {
      setConversations((oldConversations) => ({
        ...oldConversations,
        [ConversationTab.customer]: [
          new Conversation(conversation),
          ...oldConversations[ConversationTab.customer],
        ],
      }));

      setTotals((oldTotals) => ({
        ...oldTotals,
        [ConversationTab.customer]: oldTotals[ConversationTab.customer] + 1,
      }));
    }
  };

  const getConversations = useRefCallback(
    async (tab: ConversationTab, reset?: boolean) => {
      setLoading((oldLoading) => ({ ...oldLoading, [tab]: true }));

      try {
        const response = await loadConversations(
          appSettings.appId,
          undefined,
          tab === ConversationTab.account,
          {
            cursor: (!reset && cursor[tab]) || undefined,
            filters: filters[tab],
            sort: sorts[tab],
          }
        );
        setTotals((oldTotals) => ({ ...oldTotals, [tab]: response.total }));

        const conversations = response.data.map(
          (conversation) => new Conversation(conversation)
        );

        setConversations((oldConversations) => ({
          ...oldConversations,
          [tab]: [...(reset ? [] : oldConversations[tab]), ...conversations],
        }));

        setCursor((oldCursor) => ({
          ...oldCursor,
          [tab]: (reset ? 0 : oldCursor[tab]) + response.data.length,
        }));
      } finally {
        setLoading((oldLoading) => ({ ...oldLoading, [tab]: false }));
      }
    }
  );

  const createConversation = useRefCallback(
    async (payload: TCreateConversationPayload) => {
      const newConversation = await createConversationApi(
        appSettings.appId,
        payload
      );

      if (newConversation) updateConversationsOnTicketSubmit(newConversation);
    }
  );

  const conversationsCountByStatus = useMemo<
    Record<ConversationTab, [number, number]>
  >(
    () => ({
      [ConversationTab.customer]: getConversationsCountByStatus(
        conversations[ConversationTab.customer]
      ),
      [ConversationTab.account]: getConversationsCountByStatus(
        conversations[ConversationTab.account]
      ),
    }),
    [conversations]
  );

  const value: CustomerPortalContextProps = {
    getConversations,
    conversations,
    conversationsCountByStatus,
    totals,
    loading,
    createConversation,
    customer,
    filters,
    sorts,
    setFilter,
    setSort,
  };

  return (
    <CustomerPortalContext.Provider value={value}>
      {children}
    </CustomerPortalContext.Provider>
  );
};

export default useCustomerPortalContext;

export enum ConversationTab {
  customer = "customer",
  account = "account",
}

// [openConversationsCount, closedConversationsCount]
const getConversationsCountByStatus = (
  conversations: Conversation[]
): [number, number] =>
  conversations.reduce(
    (acc, current) => {
      let index = ConversationStatusIndex.open;
      if (current.status === "CLOSED") {
        index = ConversationStatusIndex.closed;
      }

      acc[index] = (acc[index] || 0) + 1;
      return acc;
    },
    [0, 0]
  );

// #region Types

export enum ConversationStatusIndex {
  open = 0,
  closed = 1,
}

type CustomerPortalContextProps = {
  getConversations: (tab: ConversationTab, reset?: boolean) => Promise<void>;
  conversations: Record<ConversationTab, Conversation[]>;
  conversationsCountByStatus: Record<ConversationTab, [number, number]>;
  totals: Record<ConversationTab, number>;
  loading: Record<ConversationTab, boolean>;
  createConversation: (payload: TCreateConversationPayload) => Promise<void>;
  customer: Customer | null;
  filters: Record<ConversationTab, TConversationListFilter | null>;
  setFilter: (tab: ConversationTab, filter?: TConversationListFilter) => void;
  sorts: Record<ConversationTab, string>;
  setSort: (tab: ConversationTab, sort: string) => void;
};

// #endregion
